import {
  Goal,
  goalsArchivedSectionID,
  goalsCompletedSectionID,
  ID,
  rootId,
  TaskListItemTypes,
  TaskListTreeItem,
} from '@griply/types';

import { getArchivedGoalIds } from '../../derived-goal-state';
import { buildGoalsById } from '../../get-goal-life-area';
import {
  compareDeadlineQuarterKeys,
  getDeadlineQuarterKey,
} from '../deadline-quarters';
import { getPresentParentIds } from '../get-present-parent-ids';
import { orderGoalTreeChildren } from './order-goal-tree-children';
import { BuildGoalListTreeOptions } from './types';

export type BuildDeadlineGoalListTreeOptions = BuildGoalListTreeOptions & {
  // Off: every goal sits in its own deadline quarter; parents are shown as the row's overline instead.
  nestSubGoals?: boolean;
};

// Quarter sections use the quarter key (e.g. '2026-Q1' / 'no-deadline') directly
// as their tree id; goal ids are Firestore ids and never collide with these. The
// section node `name` is the raw key — consumers resolve the display label.
export const buildDeadlineGoalListTree = (
  goals: Goal[],
  {
    hideArchived,
    hideCompleted,
    allGoals,
    links,
    goalChildrenByParentId,
    nestSubGoals = true,
  }: BuildDeadlineGoalListTreeOptions,
): Record<ID, TaskListTreeItem> => {
  const archivedIds = getArchivedGoalIds(allGoals, { links });
  const activeGoals: Goal[] = [];
  const archivedGoals: ID[] = [];
  const completedGoals: ID[] = [];

  goals.forEach((goal) => {
    if (hideArchived && archivedIds.has(goal.id)) {
      return;
    }
    if (goal.archivedAt) {
      archivedGoals.push(goal.id);
      return;
    }
    // A completed goal never sits inline among its live siblings; it moves to its own section.
    if (goal.completedAt) {
      if (!hideCompleted) {
        completedGoals.push(goal.id);
      }
      return;
    }
    activeGoals.push(goal);
  });

  const treeItems: Record<ID, TaskListTreeItem> = {};

  goals.forEach((goal) => {
    treeItems[goal.id] = {
      id: goal.id,
      type: TaskListItemTypes.Goal,
      name: goal.name,
      children: [],
      isFolder: nestSubGoals,
    };
  });

  const goalsById = buildGoalsById(allGoals);
  const quarterKeys = new Set<string>();

  activeGoals.forEach((goal) => {
    const parentIds = nestSubGoals
      ? getPresentParentIds(goal, (id) => !!treeItems[id], {
          links,
          goalsById,
        })
      : [];

    if (parentIds.length) {
      // A goal nests under EVERY parent present in the set; the tree list keys nodes by path.
      parentIds.forEach((parentId) => {
        treeItems[parentId].children?.push(goal.id);
      });
      return;
    }

    // Archived through an ancestor the filter dropped — no parent left to nest under.
    if (archivedIds.has(goal.id)) {
      archivedGoals.push(goal.id);
      return;
    }

    const quarterKey = getDeadlineQuarterKey(goal.deadline);
    if (!treeItems[quarterKey]) {
      treeItems[quarterKey] = {
        id: quarterKey,
        type: TaskListItemTypes.LifeArea,
        name: quarterKey,
        children: [],
        isFolder: true,
      };
      quarterKeys.add(quarterKey);
    }
    treeItems[quarterKey].children?.push(goal.id);
  });

  if (completedGoals.length) {
    treeItems[goalsCompletedSectionID] = {
      id: goalsCompletedSectionID,
      type: TaskListItemTypes.LifeArea,
      name: '',
      children: completedGoals,
      isFolder: true,
    };
  }

  if (archivedGoals.length) {
    treeItems[goalsArchivedSectionID] = {
      id: goalsArchivedSectionID,
      type: TaskListItemTypes.LifeArea,
      name: '',
      children: archivedGoals,
      isFolder: true,
    };
  }

  const rootChildren = [...quarterKeys].sort(compareDeadlineQuarterKeys);

  if (completedGoals.length) {
    rootChildren.push(goalsCompletedSectionID);
  }

  if (archivedGoals.length) {
    rootChildren.push(goalsArchivedSectionID);
  }

  orderGoalTreeChildren(treeItems, goalChildrenByParentId);

  return {
    [rootId]: {
      id: rootId,
      name: 'Root',
      type: TaskListItemTypes.Root,
      children: rootChildren,
    },
    ...treeItems,
  };
};
